import { Text, View, StyleSheet } from "@react-pdf/renderer";
import { useTranslation } from "react-i18next";
import { AiAssignmentResponseDto } from "@packages/api/madlenAPI.schemas";
import MarkdownRenderer from "../custom-markdown";
import PdfLayout from "../pdf/PdfLayout";
import { commonStyles } from "../pdf/CommonPdfStyles";
import { PdfCustomizationSettings } from "@features/common/components/pdf-customization-modal";
import { scaleFontSize } from "../pdf/fontSizeAdjustment";
interface AiAssignmentCardPDFProps {
  assignment: AiAssignmentResponseDto & { imageUrl: string | null };
  customizationSettings: PdfCustomizationSettings;
}

const styles = StyleSheet.create({
  metadata: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 20,
    flexWrap: "wrap",
  },
  metaItem: {
    fontSize: 10,
    color: "#4B5563", // gray-600
    marginBottom: 8,
    width: "50%",
  },
  section: {
    marginBottom: 20,
    padding: 16,
    backgroundColor: "#FFFBEB", // amber-50
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#FEF3C7", // amber-100
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: "bold",
    marginBottom: 10,
    color: "#D97706", // amber-600
    paddingBottom: 5,
    borderBottom: "1 solid #FDE68A", // amber-200
  },
  rubricSection: {
    marginBottom: 16,
    backgroundColor: "#FFFFFF",
    borderRadius: 6,
    padding: 10,
    borderWidth: 1,
    borderColor: "#FDE68A", // amber-200
  },
  rubricTitle: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#92400E", // amber-800
    marginBottom: 3,
  },
  rubricWeight: {
    fontSize: 12,
    fontWeight: "normal",
    color: "#D97706", // amber-600
    marginBottom: 8,
    backgroundColor: "#FEF3C7", // amber-100
    padding: "3 6",
    borderRadius: 4,
    alignSelf: "flex-start",
  },
  rubricLevelsContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 10,
  },
  rubricLevelContainer: {
    flex: 1,
    minWidth: "30%",
    borderRadius: 4,
    padding: 8,
    marginBottom: 8,
  },
  excellentLevelContainer: {
    backgroundColor: "#F0FDF4", // green-50
    borderWidth: 1,
    borderColor: "#DCFCE7", // green-100
  },
  satisfactoryLevelContainer: {
    backgroundColor: "#FFFBEB", // amber-50
    borderWidth: 1,
    borderColor: "#FEF3C7", // amber-100
  },
  needsImprovementLevelContainer: {
    backgroundColor: "#FEF2F2", // red-50
    borderWidth: 1,
    borderColor: "#FEE2E2", // red-100
  },
  rubricLevelHeader: {
    flexDirection: "row",
    alignItems: "center",
    paddingBottom: 4,
    borderBottomWidth: 1,
    marginBottom: 4,
  },
  excellentLevelHeader: {
    borderBottomColor: "#DCFCE7", // green-100
  },
  satisfactoryLevelHeader: {
    borderBottomColor: "#FEF3C7", // amber-100
  },
  needsImprovementLevelHeader: {
    borderBottomColor: "#FEE2E2", // red-100
  },
  rubricLevelIndicator: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginRight: 6,
  },
  excellentLevelIndicator: {
    backgroundColor: "#22C55E", // green-500
  },
  satisfactoryLevelIndicator: {
    backgroundColor: "#F59E0B", // amber-500
  },
  needsImprovementLevelIndicator: {
    backgroundColor: "#EF4444", // red-500
  },
  rubricLevelTitle: {
    fontSize: 12,
    fontWeight: "bold",
  },
  excellentLevelTitle: {
    color: "#16A34A", // green-600
  },
  satisfactoryLevelTitle: {
    color: "#D97706", // amber-600
  },
  needsImprovementLevelTitle: {
    color: "#DC2626", // red-600
  },
  rubricLevelDescription: {
    fontSize: 10,
    color: "#4B5563", // gray-600
  },
  itemContainer: {
    marginBottom: 10,
    paddingLeft: 10,
    borderLeft: "2 solid #F59E0B", // amber-500
  },
  itemRowContainer: {
    marginBottom: 10,
    paddingLeft: 10,
    borderLeft: "2 solid #F59E0B", // amber-500
    display: "flex",
    flexDirection: "row",
    gap: 10,
    alignItems: "center",
    justifyContent: "flex-start",
  },
  stepContainer: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginBottom: 12,
  },
  stepNumber: {
    backgroundColor: "#F59E0B",
    color: "white",
    fontSize: 9,
    fontWeight: "bold",
    padding: 4,
    borderRadius: 10,
    marginRight: 8,
    textAlign: "center",
    width: 20,
    height: 20,
  },
  stepContent: {
    flex: 1,
  },
  reflectionContainer: {
    marginBottom: 12,
    backgroundColor: "#FFFFFF",
    borderRadius: 6,
    padding: 10,
    borderWidth: 1,
    borderColor: "#FDE68A", // amber-200
  },
  reflectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 8,
  },
  reflectionNumber: {
    backgroundColor: "#F59E0B",
    color: "white",
    fontSize: 9,
    fontWeight: "bold",
    padding: 4,
    borderRadius: 10,
    marginRight: 8,
    textAlign: "center",
    width: 20,
    height: 20,
  },
  reflectionTitle: {
    fontSize: 12,
    fontWeight: "bold",
    color: "#92400E", // amber-800
  },
  promptContainer: {
    marginBottom: 12,
    backgroundColor: "#F9FAFB", // gray-50
    borderRadius: 6,
    padding: 10,
    borderWidth: 1,
    borderColor: "#E5E7EB", // gray-200
    fontFamily: "Courier",
  },
  promptHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 8,
  },
  promptNumber: {
    backgroundColor: "#D97706", // amber-600
    color: "white",
    fontSize: 9,
    fontWeight: "bold",
    padding: 4,
    borderRadius: 10,
    marginRight: 8,
    textAlign: "center",
    width: 20,
    height: 20,
  },
  promptTitle: {
    fontSize: 12,
    fontWeight: "bold",
    color: "#92400E", // amber-800
  },
  promptContent: {
    fontSize: 9,
    color: "#4B5563", // gray-600
    fontFamily: "Courier",
  },
  footer: {
    marginTop: 20,
    borderTop: "1 solid #EAEAEA",
    paddingTop: 10,
    fontSize: 8,
    color: "#9CA3AF", // gray-400
    textAlign: "center",
  },
  ethicalTipsContainer: {
    marginTop: 16,
    backgroundColor: "#FEF3C7", // amber-100
    borderRadius: 6,
    padding: 10,
    borderWidth: 1,
    borderColor: "#FDE68A", // amber-200
  },
  ethicalTipsTitle: {
    fontSize: 12,
    fontWeight: "bold",
    color: "#92400E", // amber-800
    marginBottom: 8,
  },
  ethicalTipItem: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginBottom: 6,
  },
  ethicalTipBullet: {
    width: 4,
    height: 4,
    borderRadius: 2,
    backgroundColor: "#F59E0B", // amber-500
    marginRight: 6,
    marginTop: 6,
  },
  ethicalTipText: {
    fontSize: 10,
    color: "#92400E", // amber-800
    flex: 1,
  },
});

const AiAssignmentCardPDF = ({
  assignment,
  customizationSettings,
}: AiAssignmentCardPDFProps) => {
  const { t } = useTranslation();
  const sectionTitleStyle = [
    styles.sectionTitle,
    {
      fontSize: scaleFontSize(
        styles.sectionTitle.fontSize,
        customizationSettings?.fontSize,
      ),
    },
  ];

  // Helper function to scale font size
  const scaledFontSize = (size: number) =>
    scaleFontSize(size, customizationSettings?.fontSize);

  return (
    <PdfLayout
      courseName={assignment.courseName}
      gradeLevel={assignment.gradeLevel}
      topic={assignment.title || t("aiAssignmentDesigner.title")}
      imageUrl={assignment.imageUrl || undefined}
      headerTextColor="#D97706" // amber-600
      title={assignment.title}
      author={assignment.authorName}
      createdDate={assignment.createdAt}
      subject={assignment.courseName}
      customizationSettings={customizationSettings}
    >
      {/* Student Instructions Section */}
      {assignment.studentInstructions && (
        <View style={styles.section} minPresenceAhead={100}>
          <Text style={sectionTitleStyle}>
            {t("aiAssignmentDesigner.studentInstructions")}
          </Text>

          {/* İlk Yarı */}
          <View style={styles.itemContainer}>
            <MarkdownRenderer
              content={assignment.studentInstructions}
              customStyle={{
                text: {
                  text: {
                    ...commonStyles.sectionContent,
                    fontSize: scaledFontSize(
                      commonStyles.sectionContent.fontSize,
                    ),
                  },
                  container: {
                    marginBottom: 12,
                    paddingVertical: 2,
                  },
                },
                listItem: {
                  text: {
                    ...commonStyles.sectionContent,
                    fontSize: scaledFontSize(
                      commonStyles.sectionContent.fontSize,
                    ),
                  },
                  container: {
                    marginBottom: 12,
                  },
                },
              }}
            />
          </View>

          {/* İkinci Yarı - Ethical Usage Tips (Yeni Sayfada) */}
          <View break minPresenceAhead={300}>
            <View style={styles.ethicalTipsContainer} wrap={false}>
              <Text
                style={[
                  styles.ethicalTipsTitle,
                  {
                    fontSize: scaledFontSize(styles.ethicalTipsTitle.fontSize),
                  },
                ]}
              >
                {t("bloomsTaxonomyActivity.ethicalUsageTips")}
              </Text>
              <View style={styles.ethicalTipItem}>
                <View style={styles.ethicalTipBullet} />
                <Text
                  style={[
                    styles.ethicalTipText,
                    {
                      fontSize: scaledFontSize(styles.ethicalTipText.fontSize),
                    },
                  ]}
                >
                  {t("bloomsTaxonomyActivity.ethicTip1")}
                </Text>
              </View>
              <View style={styles.ethicalTipItem}>
                <View style={styles.ethicalTipBullet} />
                <Text
                  style={[
                    styles.ethicalTipText,
                    {
                      fontSize: scaledFontSize(styles.ethicalTipText.fontSize),
                    },
                  ]}
                >
                  {t("bloomsTaxonomyActivity.ethicTip2")}
                </Text>
              </View>
              <View style={styles.ethicalTipItem}>
                <View style={styles.ethicalTipBullet} />
                <Text
                  style={[
                    styles.ethicalTipText,
                    {
                      fontSize: scaledFontSize(styles.ethicalTipText.fontSize),
                    },
                  ]}
                >
                  {t("bloomsTaxonomyActivity.ethicTip3")}
                </Text>
              </View>
              <View style={styles.ethicalTipItem}>
                <View style={styles.ethicalTipBullet} />
                <Text
                  style={[
                    styles.ethicalTipText,
                    {
                      fontSize: scaledFontSize(styles.ethicalTipText.fontSize),
                    },
                  ]}
                >
                  {t("bloomsTaxonomyActivity.ethicTip4")}
                </Text>
              </View>
            </View>
          </View>
        </View>
      )}

      {/* Implementation Steps Section */}
      {assignment.implementationSteps &&
        assignment.implementationSteps.length > 0 && (
          <View style={styles.section} break minPresenceAhead={300}>
            <Text style={sectionTitleStyle}>
              {t("aiAssignmentDesigner.implementationSteps")}
            </Text>
            {assignment.implementationSteps.map((step, idx) => (
              <View
                key={idx}
                style={styles.stepContainer}
                minPresenceAhead={150}
              >
                <Text
                  style={[
                    styles.stepNumber,
                    { fontSize: scaledFontSize(styles.stepNumber.fontSize) },
                  ]}
                >
                  {idx + 1}
                </Text>
                <View style={styles.stepContent}>
                  <MarkdownRenderer
                    content={step}
                    customStyle={{
                      text: {
                        text: {
                          ...commonStyles.sectionContent,
                          fontSize: scaledFontSize(
                            commonStyles.sectionContent.fontSize,
                          ),
                        },
                      },
                    }}
                  />
                </View>
              </View>
            ))}
          </View>
        )}

      {/* Reflection Questions Section */}
      {assignment.reflectionQuestions &&
        assignment.reflectionQuestions.length > 0 && (
          <View style={styles.section} break minPresenceAhead={300}>
            <Text style={sectionTitleStyle}>
              {t("aiAssignmentDesigner.reflectionQuestions")}
            </Text>
            {assignment.reflectionQuestions.map((question, idx) => (
              <View
                key={idx}
                style={styles.reflectionContainer}
                minPresenceAhead={200}
              >
                <View style={styles.reflectionHeader}>
                  <Text
                    style={[
                      styles.reflectionNumber,
                      {
                        fontSize: scaledFontSize(
                          styles.reflectionNumber.fontSize,
                        ),
                      },
                    ]}
                  >
                    {idx + 1}
                  </Text>
                  <Text
                    style={[
                      styles.reflectionTitle,
                      {
                        fontSize: scaledFontSize(
                          styles.reflectionTitle.fontSize,
                        ),
                      },
                    ]}
                  >
                    {t("aiAssignmentDesigner.question")}
                  </Text>
                </View>
                <MarkdownRenderer
                  content={question}
                  customStyle={{
                    text: {
                      text: {
                        ...commonStyles.sectionContent,
                        fontSize: scaledFontSize(
                          commonStyles.sectionContent.fontSize,
                        ),
                      },
                    },
                  }}
                />
              </View>
            ))}
          </View>
        )}

      {/* Grading Rubric Section */}
      {assignment.gradingRubric && assignment.gradingRubric.length > 0 && (
        <View style={styles.section} break minPresenceAhead={400}>
          <Text style={sectionTitleStyle}>
            {t("aiAssignmentDesigner.gradingRubric")}
          </Text>
          {assignment.gradingRubric.map((rubricItem, idx) => (
            <View key={idx} style={styles.rubricSection} minPresenceAhead={250}>
              <Text
                style={[
                  styles.rubricTitle,
                  { fontSize: scaledFontSize(styles.rubricTitle.fontSize) },
                ]}
              >
                {rubricItem.criterion}
              </Text>
              <Text
                style={[
                  styles.rubricWeight,
                  { fontSize: scaledFontSize(styles.rubricWeight.fontSize) },
                ]}
              >
                {t("aiAssignmentDesigner.weights")}: {rubricItem.weights}
              </Text>

              <View style={styles.rubricLevelsContainer}>
                {Object.entries(rubricItem.levels || {})
                  .sort((a, b) => {
                    // Sort numerically to ensure best level (0) comes first
                    const levelA = parseInt(a[0]);
                    const levelB = parseInt(b[0]);
                    return levelA - levelB;
                  })
                  .map(([level, description], levelIdx) => {
                    let containerStyle,
                      headerStyle,
                      indicatorStyle,
                      titleStyle,
                      labelText;

                    if (levelIdx === 0) {
                      containerStyle = styles.excellentLevelContainer;
                      headerStyle = styles.excellentLevelHeader;
                      indicatorStyle = styles.excellentLevelIndicator;
                      titleStyle = styles.excellentLevelTitle;
                      labelText = t(
                        "aiAssignmentDesigner.performanceLevels.excellent",
                      );
                    } else if (levelIdx === 1) {
                      containerStyle = styles.satisfactoryLevelContainer;
                      headerStyle = styles.satisfactoryLevelHeader;
                      indicatorStyle = styles.satisfactoryLevelIndicator;
                      titleStyle = styles.satisfactoryLevelTitle;
                      labelText = t(
                        "aiAssignmentDesigner.performanceLevels.satisfactory",
                      );
                    } else {
                      containerStyle = styles.needsImprovementLevelContainer;
                      headerStyle = styles.needsImprovementLevelHeader;
                      indicatorStyle = styles.needsImprovementLevelIndicator;
                      titleStyle = styles.needsImprovementLevelTitle;
                      labelText = t(
                        "aiAssignmentDesigner.performanceLevels.needsImprovement",
                      );
                    }

                    return (
                      <View
                        key={levelIdx}
                        style={[styles.rubricLevelContainer, containerStyle]}
                        wrap={false}
                      >
                        <View style={[styles.rubricLevelHeader, headerStyle]}>
                          <View
                            style={[
                              styles.rubricLevelIndicator,
                              indicatorStyle,
                            ]}
                          />
                          <Text
                            style={[
                              styles.rubricLevelTitle,
                              titleStyle,
                              {
                                fontSize: scaledFontSize(
                                  styles.rubricLevelTitle.fontSize,
                                ),
                              },
                            ]}
                          >
                            {labelText}
                          </Text>
                        </View>
                        <MarkdownRenderer
                          content={description}
                          customStyle={{
                            text: {
                              text: {
                                ...styles.rubricLevelDescription,
                                fontSize: scaledFontSize(
                                  styles.rubricLevelDescription.fontSize,
                                ),
                              },
                            },
                          }}
                        />
                      </View>
                    );
                  })}
              </View>
            </View>
          ))}
        </View>
      )}

      {/* AI Prompt Examples Section */}
      {assignment.aiPromptExamples &&
        assignment.aiPromptExamples.length > 0 && (
          <View style={styles.section} break minPresenceAhead={300}>
            <Text style={sectionTitleStyle}>
              {t("aiAssignmentDesigner.aiPromptExamples")}
            </Text>
            {assignment.aiPromptExamples.map((example, idx) => (
              <View
                key={idx}
                style={styles.promptContainer}
                minPresenceAhead={200}
                wrap={false}
              >
                <View style={styles.promptHeader}>
                  <Text
                    style={[
                      styles.promptNumber,
                      {
                        fontSize: scaledFontSize(styles.promptNumber.fontSize),
                      },
                    ]}
                  >
                    {idx + 1}
                  </Text>
                  <Text
                    style={[
                      styles.promptTitle,
                      { fontSize: scaledFontSize(styles.promptTitle.fontSize) },
                    ]}
                  >
                    {t("aiAssignmentDesigner.example")} {idx + 1}
                  </Text>
                </View>
                <Text
                  style={[
                    styles.promptContent,
                    { fontSize: scaledFontSize(styles.promptContent.fontSize) },
                  ]}
                >
                  {example}
                </Text>
              </View>
            ))}
          </View>
        )}
    </PdfLayout>
  );
};

export default AiAssignmentCardPDF;
